import { ConsequenceNote, SkeletonSet } from '@cwos/ui-admin';
import { SettingsActions, SettingsCard, SettingsField } from '../settings/common.js';
import {
  BREADCRUMB_COLLECTION_WHEN,
  BREADCRUMBS_OFF_WARNING,
  breadcrumbsOnWarning,
  CLOCK_COLLECTION_WHEN,
  CLOCK_REQUIRED_HONESTY,
  MODE_OFF_TRACKING_NOTE,
  ORGANIZATION_AUDIENCE,
  workerBreadcrumbCardQuote,
} from './copy.js';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { PolicySchedule } from './PolicySchedule.js';
import { scheduleStatementOf, usePolicyForm, validateDraft } from './policyForm.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Location tracking — the collection-depth story (directive §4): what is
 * collected at the clock punch, and what is collected while a shift runs.
 * Both controls are real `location_policy` columns; the worker-visible
 * consequence QUOTES the mobile PolicyCard's fixed copy rather than
 * re-implementing it.
 */
export function LocationTrackingPage() {
  const form = usePolicyForm(null);

  return (
    <LocationSettingsShell active="tracking" dirty={form.dirty}>
      {form.loading ? (
        <SettingsCard>
          <SkeletonSet surface="table" rows={4} />
        </SettingsCard>
      ) : form.loadError || !form.draft || !form.policy ? (
        <SettingsCard>
          <LoadFailure
            error={form.loadFailure}
            message="We couldn’t load the policy just now."
            onRetry={form.refetch}
          />
        </SettingsCard>
      ) : (
        <>
          {form.draft.mode === 'OFF' ? (
            <SettingsCard>
              <p className="rn-muted" role="status">
                {MODE_OFF_TRACKING_NOTE}
              </p>
            </SettingsCard>
          ) : null}

          <SettingsCard>
            <h2 className="rn-card-title">At the clock punch</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              When collection happens: {CLOCK_COLLECTION_WHEN}.
            </p>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.clockInLocationRequired}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ clockInLocationRequired: e.target.checked })}
                />
                <span>Ask the device for a location at clock-in</span>
              </label>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.clockOutLocationRequired}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ clockOutLocationRequired: e.target.checked })}
                />
                <span>Ask the device for a location at clock-out</span>
              </label>
              <p className="rn-muted" style={{ fontSize: 13 }}>
                {CLOCK_REQUIRED_HONESTY}
              </p>
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">While a shift is running</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              When collection happens: {BREADCRUMB_COLLECTION_WHEN}.
            </p>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.breadcrumbEnabled}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ breadcrumbEnabled: e.target.checked })}
                />
                <span>Collect location points during shifts</span>
              </label>

              {/*
                BRANCHED, for the reason `copy.ts` gives at length: "as soon as
                you save" is true of an unscheduled save and false of a
                scheduled one, and this form can now make either. A `null`
                statement is the operator mid-choice — no note, rather than the
                wrong one.
              */}
              {form.draft.breadcrumbEnabled &&
              !form.policy.breadcrumbEnabled &&
              scheduleStatementOf(form.draft) !== null ? (
                <ConsequenceNote
                  text={breadcrumbsOnWarning(
                    scheduleStatementOf(form.draft)!,
                    ORGANIZATION_AUDIENCE,
                  )}
                />
              ) : null}
              {!form.draft.breadcrumbEnabled && form.policy.breadcrumbEnabled ? (
                <ConsequenceNote text={BREADCRUMBS_OFF_WARNING} />
              ) : null}

              <SettingsField
                label="Seconds between points"
                hint="Between 30 seconds and 24 hours (86,400 seconds)."
                error={validateDraft(form.draft).breadcrumbIntervalSeconds}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.draft.breadcrumbIntervalSeconds}
                  disabled={!form.canManage || !form.draft.breadcrumbEnabled}
                  onChange={(e) => form.set({ breadcrumbIntervalSeconds: e.target.value })}
                  style={{ maxWidth: 120 }}
                />
              </SettingsField>

              {form.draft.breadcrumbEnabled && form.valid ? (
                /*
                 * THE SENTENCE THIS WHOLE CARD TURNS ON.
                 *
                 * It read "Workers see this on their policy card:" — present
                 * tense, unconditional, and false. `PolicyCard` was exported
                 * from `@cwos/ui-mobile` and mounted on no file under
                 * `apps/worker-mobile/`, so no worker saw it anywhere. A
                 * compliance-grade claim that a disclosure obligation has been
                 * discharged, made to the person deciding whether to switch
                 * location tracking on for other people.
                 *
                 * The card is mounted now (`app/more.tsx`), which makes the
                 * claim true — but only in the tense the product can support.
                 * The worker READS it when they open that screen; nothing
                 * pushes it to them and nothing interrupts a running shift. So
                 * the sentence says "in the app, on their More screen", not
                 * "see", and BREADCRUMBS_ON_WARNING above carries the timing.
                 */
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  On the worker’s More screen, their policy card reads: “
                  {workerBreadcrumbCardQuote(Number(form.draft.breadcrumbIntervalSeconds))}”
                </p>
              ) : null}
            </div>
          </SettingsCard>

          <SettingsCard>
            <PolicySchedule form={form} audience={ORGANIZATION_AUDIENCE} />
          </SettingsCard>

          {form.canManage ? (
            <SettingsCard>
              <SettingsActions
                dirty={form.dirty}
                valid={form.valid}
                saving={form.saving}
                saved={form.saved}
                error={form.saveError}
                onSave={form.save}
                onCancel={form.cancel}
              />
            </SettingsCard>
          ) : null}
        </>
      )}
    </LocationSettingsShell>
  );
}
